import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Rocket, ShieldCheck, ArrowLeft, Copy, Download, ExternalLink, CheckCircle2, Send, StickyNote,
  CalendarClock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';
import { usePitaLogo } from '../../components/PitaCTA';

const STATUS_STYLES = {
  'draft-needs-review': 'bg-amber-50 text-amber-900 border-amber-300',
  approved: 'bg-blue-50 text-blue-800 border-blue-300',
  posted: 'bg-emerald-50 text-emerald-800 border-emerald-300',
};

const KIND_ORDER = ['Announcement', 'Social', 'Press Release', 'Asset', 'Link'];

const PackageItem = ({ item, statuses, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(item.copy || '');
      toast.success('Copied to clipboard');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = item.copy || '';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Copied to clipboard');
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/launch-package/${item.id}`, { status });
      toast.success(`Status: ${statuses[status]}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setBusy(false);
  };

  const markPosted = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/launch-package/${item.id}/mark-posted`);
      toast.success('Marked as posted / sent');
      onSaved();
    } catch { toast.error('Update failed'); }
    setBusy(false);
  };

  const saveNotes = async (notes) => {
    if (notes === (item.notes || '')) return;
    try {
      await api.put(`/admin/launch-package/${item.id}`, { notes });
      toast.success('Notes saved');
      onSaved();
    } catch { toast.error('Notes save failed'); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`package-item-${item.key}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744] text-sm leading-snug">{item.title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{item.kind} · Last updated {String(item.updated_at || '').slice(0, 10)}{item.posted_at ? ` · Posted ${String(item.posted_at).slice(0, 10)}` : ''}</p>
        </div>
        <Badge variant="outline" className={`text-[10px] font-bold shrink-0 ${STATUS_STYLES[item.status]}`} data-testid={`package-status-${item.key}`}>{statuses[item.status]}</Badge>
      </div>
      {item.hint && <p className="text-[11px] text-slate-500 mt-1.5">{item.hint}</p>}

      {expanded && (
        <pre className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-72 overflow-y-auto" data-testid={`package-copy-${item.key}`}>{item.copy}</pre>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => setExpanded((v) => !v)} className="h-9 text-xs font-bold btn-press" data-testid={`package-toggle-${item.key}`}>
          {expanded ? 'Hide Copy' : 'View Copy'}
        </Button>
        <Button size="sm" variant="outline" onClick={copyText} className="h-9 text-xs font-bold btn-press" data-testid={`package-copy-btn-${item.key}`}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy Text
        </Button>
        {item.link && (
          <Button size="sm" variant="outline" onClick={() => window.open(item.download ? fileUrl(item.link) : item.link, '_blank', 'noopener')} className="h-9 text-xs font-bold btn-press" data-testid={`package-open-${item.key}`}>
            {item.download ? <Download className="h-3.5 w-3.5 mr-1" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />} {item.download ? 'Download' : 'Open'}
          </Button>
        )}
        <Select value={item.status} onValueChange={setStatus} disabled={busy}>
          <SelectTrigger className="h-9 w-44 text-xs font-bold" data-testid={`package-status-select-${item.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        {item.status !== 'posted' && (
          <Button size="sm" onClick={markPosted} disabled={busy} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`package-mark-posted-${item.key}`}>
            <Send className="h-3.5 w-3.5 mr-1" /> Mark Posted / Sent
          </Button>
        )}
      </div>

      <div className="mt-3">
        <label className="text-[11px] font-bold text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Notes</label>
        <Input defaultValue={item.notes || ''} onBlur={(e) => saveNotes(e.target.value)} placeholder="e.g. Sent to Kawartha 411 on…" className="h-10 text-sm mt-1" data-testid={`package-notes-${item.key}`} />
      </div>
    </div>
  );
};

const PLAN_STATUS_STYLES = {
  draft: 'bg-amber-50 text-amber-900 border-amber-300',
  ready: 'bg-blue-50 text-blue-800 border-blue-300',
  posted: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  skipped: 'bg-slate-100 text-slate-500 border-slate-300',
};

const PlanningRow = ({ post, statuses, platforms, onSaved }) => {
  const [busy, setBusy] = useState(false);

  const update = async (patch) => {
    setBusy(true);
    try {
      await api.put(`/admin/launch-package/announcement-planning/${post.id}`, patch);
      toast.success('Plan saved — nothing auto-posts');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    setBusy(false);
  };

  const markPosted = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/launch-package/announcement-planning/${post.id}/mark-posted`);
      toast.success('Marked as posted');
      onSaved();
    } catch { toast.error('Update failed'); }
    setBusy(false);
  };

  const copyText = async () => {
    const text = post.body || post.title;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Copied to clipboard');
    }
  };

  const slug = (post.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`planning-row-${slug}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744] text-sm leading-snug">{post.title}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{post.phase || 'Launch post'}{post.posted_at ? ` · Posted ${String(post.posted_at).slice(0, 10)}` : ''}</p>
        </div>
        <Badge variant="outline" className={`text-[10px] font-bold shrink-0 ${PLAN_STATUS_STYLES[post.planning_status] || PLAN_STATUS_STYLES.draft}`} data-testid={`planning-status-${slug}`}>
          {statuses[post.planning_status] || 'Draft'}{!post.planned_date ? ' / Unscheduled' : ''}
        </Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">Planned date</label>
          <Input type="date" defaultValue={post.planned_date || ''} onBlur={(e) => { if (e.target.value !== (post.planned_date || '')) update({ planned_date: e.target.value }); }} className="h-10 text-sm mt-0.5" disabled={busy} data-testid={`planning-date-${slug}`} />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">Planned time</label>
          <Input type="time" defaultValue={post.planned_time || ''} onBlur={(e) => { if (e.target.value !== (post.planned_time || '')) update({ planned_time: e.target.value }); }} className="h-10 text-sm mt-0.5" disabled={busy} data-testid={`planning-time-${slug}`} />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">Platform</label>
          <Select value={post.platform} onValueChange={(v) => update({ platform: v })} disabled={busy}>
            <SelectTrigger className="h-10 text-sm mt-0.5" data-testid={`planning-platform-${slug}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {platforms.map((p) => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">Status</label>
          <Select value={post.planning_status} onValueChange={(v) => update({ planning_status: v })} disabled={busy}>
            <SelectTrigger className="h-10 text-sm mt-0.5" data-testid={`planning-status-select-${slug}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(statuses).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={copyText} className="h-9 text-xs font-bold btn-press" data-testid={`planning-copy-${slug}`}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy
        </Button>
        {post.planning_status !== 'posted' && (
          <Button size="sm" onClick={markPosted} disabled={busy} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`planning-mark-posted-${slug}`}>
            <Send className="h-3.5 w-3.5 mr-1" /> Mark as Posted
          </Button>
        )}
      </div>

      <div className="mt-3">
        <label className="text-[11px] font-bold text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Notes</label>
        <Input defaultValue={post.notes || ''} onBlur={(e) => { if (e.target.value !== (post.notes || '')) update({ notes: e.target.value }); }} placeholder="e.g. Post mid-morning for best reach…" className="h-10 text-sm mt-1" disabled={busy} data-testid={`planning-notes-${slug}`} />
      </div>
    </div>
  );
};

export default function AdminLaunchPackage() {
  const [data, setData] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [error, setError] = useState(false);
  const pitaLogo = usePitaLogo('share_cards');
  const load = useCallback(() => {
    api.get('/admin/launch-package').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
    api.get('/admin/launch-package/announcement-planning').then((r) => setPlanning(r.data)).catch(() => setPlanning(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="package-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Final Launch Package.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const grouped = KIND_ORDER.map((k) => [k, data.items.filter((i) => i.kind === k)]).filter(([, arr]) => arr.length);

  return (
    <div data-testid="admin-launch-package">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Rocket className="h-6 w-6 text-[#C9A84C]" /> Final Launch Package</h1>
          <p className="text-sm text-slate-500 mt-1">Everything Steve needs for the www.SteveLesperance.com launch — announcements, press releases, the social post, and key links, all in one place.</p>
        </div>
        <Button asChild variant="outline" className="h-11 font-bold btn-press shrink-0" data-testid="package-back-launch-btn">
          <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-5" data-testid="package-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      {pitaLogo && (
        <div className="rounded-lg bg-[#1a2744] p-3 flex gap-3 items-center mb-5" data-testid="package-pita-branding">
          <div className="bg-white rounded-lg p-1.5 h-12 w-12 flex items-center justify-center shrink-0">
            <img src={pitaLogo.url} alt={pitaLogo.alt} className="max-h-full max-w-full object-contain" data-testid="package-pita-logo" />
          </div>
          <p className="text-xs text-slate-200 leading-relaxed">
            <strong className="text-white">PITA branding active.</strong> Generated PITA posters, flyers, cards, and the Share PITA image
            use the current PITA logo. Change it anytime in <Link to="/admin/pita-settings" className="underline text-[#C9A84C] font-bold">PITA Settings</Link>.
            The <Link to="/admin/pita-settings" className="underline text-[#C9A84C] font-bold">Supporter Share Card</Link> is
            ready for supporters to download from Support PITA and the Share PITA dialog.
          </p>
        </div>
      )}

      {grouped.map(([kind, items]) => (
        <div key={kind} className="mb-6">
          <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] mb-2" data-testid={`package-group-${kind.toLowerCase().replace(/\s+/g, '-')}`}>{kind}{items.length > 1 ? `s (${items.length})` : ''}</p>
          <div className="space-y-3">
            {items.map((it) => <PackageItem key={it.id} item={it} statuses={data.statuses} onSaved={load} />)}
          </div>
        </div>
      ))}

      {/* Phase 61: Announcement Scheduling — planning only */}
      {planning && planning.posts.length > 0 && (
        <div className="mt-8" data-testid="announcement-scheduling">
          <h2 className="font-serif font-black text-xl text-[#1a2744] flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#C9A84C]" /> Announcement Scheduling</h2>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-2 mb-4" data-testid="announcement-scheduling-note">
            <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600">{planning.note}</p>
          </div>
          <div className="space-y-3">
            {planning.posts.map((p) => (
              <PlanningRow key={p.id} post={p} statuses={planning.statuses} platforms={planning.platforms} onSaved={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
